<template>
  <div>
    <MobileNavigationCollapse
      :category="category"
      :hide-collapse="!hasExpandableContent"
      :sub-category="subCategory"
      :class="classes"
      @click:item="onNavigationCollapseClick"
    >
      <!-- First level subcategories -->
      <div v-if="!contentLinks?.length">
        <div v-for="item in firstLevelLinks" :key="item.id">
          <MobileNavigationAccordionItem
            :category="item"
            :sub-category="true"
            :depth="depth + 1"
            @click:item="$emit('click:item', $event)"
          />
        </div>
      </div>

      <!-- Storyblok content -->
      <div v-else>
        <div v-for="column in contentLinks" :key="column._uid">
          <MobileNavigationCollapse
            :category="{
              name: column.Title,
              path: column.titleLink?.cached_url,
            }"
            :hide-collapse="column.Links.length === 0"
            :sub-category="true"
            @click:item="onSbNavigationCollapseClick($event, column)"
          >
            <div v-for="link in column.Links" :key="link._uid">
              <MobileNavigationAccordionItem
                :category="{ name: link.link_text, path: getLink(link) }"
                :sub-category="true"
                :depth="depth + 2"
                @click:item="$emit('click:item', $event)"
              />
            </div>
          </MobileNavigationCollapse>
        </div>
      </div>
    </MobileNavigationCollapse>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import useRouteHelpers from '~/composables/useRouteHelpers'
import type {
  SbMainNavigation,
  SbNavigationFlyoutContentColumn,
  SbNavigationFlyoutContentLink,
} from '~/cms/storyblok/types/storyblok.d.ts'

type CategoryClasses = Partial<Category> & {
  classes?: string[]
}

const props = defineProps({
  category: {
    type: Object as PropType<Category | CategoryClasses>,
    required: true,
  },
  subCategory: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  depth: {
    type: Number as PropType<number>,
    required: true,
  },
  additionalLinks: {
    type: [Object, undefined] as PropType<SbMainNavigation | undefined>,
    default: undefined,
  },
  contentLinks: {
    type: Array as PropType<SbNavigationFlyoutContentColumn[]>,
    default: undefined,
  },
})

const emit = defineEmits(['click:item'])

const { toLocalePath } = useRouteHelpers()
const { closeSideNavigation } = useUiState()
const { trackNavigationMain, trackNavigationFlyout } = useTrackingEvents()

const firstLevelLinks = computed(() => {
  if (props.contentLinks?.length) {
    return []
  }

  return [
    ...(props.category.children || []),
    ...(props.additionalLinks?.NavigationItem?.filter(
      (i) => !i.hideOnMobile,
    ).map((i) => ({
      name: i.label,
      path: toLocalePath(
        i.link?.cached_url && i.link.cached_url.startsWith('/')
          ? i.link.cached_url
          : '/' + i.link?.cached_url,
      ),
      id: i._uid,
      classes: [i.italicOnMobile ? 'italic' : ''],
    })) || []),
  ].filter((i) => i.name !== props.category.name) as CategoryClasses[]
})

const classes = computed(() =>
  'classes' in props.category ? props.category.classes : [],
)

const trackNavigation = () => {
  if (!props.category.name) return

  // Depth of 1 equates to the main navigation event on non-mobile devices,
  // while the other depth levels equate to clicks on flyout menus
  if (props.depth === 1) {
    trackNavigationMain(props.category.name)
  } else {
    trackNavigationFlyout(props.category.name, props.depth)
  }
}

const onSbNavigationCollapseClick = (
  event: MouseEvent,
  column: SbNavigationFlyoutContentColumn,
) => {
  if (column.Title) {
    trackNavigationMain(column.Title)
  }
  emit('click:item', event)
}

const onNavigationCollapseClick = (path: string) => {
  if (!path) return

  trackNavigation()

  closeSideNavigation()
  emit('click:item', path)
}

const getLink = (link: SbNavigationFlyoutContentLink) => {
  if (!link.Link?.cached_url) return ''

  const path = link.Link.cached_url.startsWith('/')
    ? link.Link.cached_url
    : '/' + link.Link.cached_url

  return toLocalePath(path).toString()
}

const hasExpandableContent = computed(() => {
  if (props.contentLinks?.length) {
    return true
  }
  return firstLevelLinks.value.length > 0
})
</script>
